"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { ScrambleText } from "motion-plus/react";
import { AnimatePresence, motion, stagger, useMotionValue, useMotionValueEvent, useSpring, useTransform, useVelocity, wrap } from "motion/react";
const PLANE_WIDTH = 320;
const PLANE_GAP = -80;
const TOTAL_PLANES = 26;
const images = Array.from({
  length: 16
}, (_, i) => `/photos/heritage/doge-404-${i}.jpg`);
const labels = ["Wow", "Many Duplicate", "Very Clone", "Such Recursion", "Amaze", "Wow", "Many Duplicate", "Very Clone", "Such Recursion", "Amaze", "Wow", "Many Duplicate", "Very Clone", "Such Recursion", "Amaze", "Wow"] as const;
const scrambleChars = "!@#$%^&*()_+-=[]{}|;:,.<>?/~`░▒▓█▀▄■□▪▫●○◆◇◈◊※†‡";
function Plane(t0) {
  const $ = _c(38);
  const {
    index,
    scrollX,
    scrollVelocity,
    isHovered,
    onHoverStart,
    onHoverEnd
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      stiffness: 400,
      damping: 25
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const hoverOffset = useSpring(0, t1);
  const planeWidth = PLANE_WIDTH + PLANE_GAP;
  const totalWidth = planeWidth * TOTAL_PLANES;
  const startPosition = index * planeWidth;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      stiffness: 300,
      damping: 20,
      mass: 0.3
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const waveOffset = useSpring(0, t2);
  let t3;
  if ($[2] !== scrollX || $[3] !== startPosition || $[4] !== waveOffset) {
    t3 = velocity => {
      const scroll = scrollX.get();
      const pos = startPosition + scroll;
      const centered = wrap(-totalWidth / 2, totalWidth / 2, pos);
      const normalizedPos = centered / (totalWidth / 2);
      const wavePhase = Math.sin(normalizedPos * Math.PI * 2);
      const waveAmount = velocity / 50 * wavePhase * 5;
      waveOffset.set(waveAmount);
    };
    $[2] = scrollX;
    $[3] = startPosition;
    $[4] = waveOffset;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useMotionValueEvent(scrollVelocity, "change", t3);
  let t4;
  let t5;
  if ($[6] !== hoverOffset || $[7] !== isHovered) {
    t4 = () => {
      hoverOffset.set(isHovered ? -30 : 0);
    };
    t5 = [isHovered, hoverOffset];
    $[6] = hoverOffset;
    $[7] = isHovered;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  if ($[10] !== hoverOffset || $[11] !== scrollX || $[12] !== startPosition || $[13] !== waveOffset) {
    t6 = () => {
      const scroll_0 = scrollX.get();
      const wave = waveOffset.get();
      const hover = hoverOffset.get();
      const pos_0 = startPosition + scroll_0;
      const centered_0 = wrap(-totalWidth / 2, totalWidth / 2, pos_0);
      const yOffset = centered_0 * -0.35 + wave + hover;
      const zOffset = centered_0 * -1.2;
      return `translate3d(${centered_0}px, ${yOffset}px, ${zOffset}px) rotateY(-50deg)`;
    };
    $[10] = hoverOffset;
    $[11] = scrollX;
    $[12] = startPosition;
    $[13] = waveOffset;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const transform = useTransform(t6);
  const labelText = labels[index % labels.length];
  const t7 = isHovered ? 100 : 1;
  const t8 = isHovered ? "brightness(1.15)" : "brightness(1)";
  let t9;
  if ($[15] !== t7 || $[16] !== t8 || $[17] !== transform) {
    t9 = {
      transform,
      zIndex: t7,
      filter: t8
    };
    $[15] = t7;
    $[16] = t8;
    $[17] = transform;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const t10 = images[index % images.length];
  const t11 = `Plane ${index}`;
  let t12;
  if ($[19] !== t10 || $[20] !== t11) {
    t12 = <div className="absolute inset-0"><img src={t10} alt={t11} className="h-180 w-120 object-cover" draggable={false} /></div>;
    $[19] = t10;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const t13 = String(index);
  let t14;
  if ($[22] !== t13) {
    t14 = t13.padStart(2, "0");
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t14) {
    t15 = <div className="font-geist-mono absolute -top-6 left-0 text-[0.625rem] font-normal tracking-wider text-[oklch(97.015%_0.00011_271.152)]">{t14}</div>;
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== isHovered || $[27] !== labelText) {
    t16 = isHovered && <motion.div className="pointer-events-none absolute top-1/2 left-full ml-3 flex items-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.15
    }}><motion.div className="label-line h-px w-30 origin-left bg-[oklch(97.015%_0.00011_271.152)]" initial={{
        scaleX: 0
      }} animate={{
        scaleX: 1
      }} exit={{
        scaleX: 0
      }} transition={{
        duration: 0.3,
        ease: "easeOut"
      }} /><div className="font-geist-mono p-[4px_8px] text-[0.625rem] font-normal tracking-wider whitespace-nowrap text-[oklch(97.015%_0.00011_271.152)] uppercase">{labelText && <ScrambleText active={isHovered} duration={stagger(0.05)} chars={scrambleChars} children={labelText} />}</div></motion.div>;
    $[26] = isHovered;
    $[27] = labelText;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t16) {
    t17 = <AnimatePresence>{t16}</AnimatePresence>;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== onHoverEnd || $[32] !== onHoverStart || $[33] !== t12 || $[34] !== t15 || $[35] !== t17 || $[36] !== t9) {
    t18 = <motion.div className="absolute flex w-80 items-center justify-center text-5xl font-bold text-[oklch(97.015%_0.00011_271.152)] [box-shadow:0_25px_50px_-12px_rgba(0,0,0,0.25)] [transition:filter_0.2s_ease] transform-3d" style={t9} onHoverStart={onHoverStart} onHoverEnd={onHoverEnd}>{t12}{t15}{t17}</motion.div>;
    $[31] = onHoverEnd;
    $[32] = onHoverStart;
    $[33] = t12;
    $[34] = t15;
    $[35] = t17;
    $[36] = t9;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  return t18;
}
export function ScrollVelocityLinkedOffset() {
  const $ = _c(16);
  const rawScrollX = useMotionValue(0);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      stiffness: 100,
      damping: 30,
      mass: 0.5
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const scrollX = useSpring(rawScrollX, t0);
  const scrollVelocity = useVelocity(scrollX);
  const containerRef = useRef(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  let t1;
  let t2;
  if ($[1] !== rawScrollX) {
    t1 = () => {
      const container = containerRef.current;
      if (!container) {
        return;
      }
      const handleWheel = e => {
        e.preventDefault();
        const delta = e.deltaX !== 0 ? e.deltaX : e.deltaY;
        rawScrollX.set(rawScrollX.get() - delta);
      };
      container.addEventListener("wheel", handleWheel, {
        passive: false
      });
      return () => container.removeEventListener("wheel", handleWheel);
    };
    t2 = [rawScrollX];
    $[1] = rawScrollX;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== rawScrollX) {
    t3 = (_, info) => {
      rawScrollX.set(rawScrollX.get() + info.delta.x * 2.5);
    };
    $[4] = rawScrollX;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="ml-[1vw] text-[clamp(32px,5vw,64px)] leading-[0.9] font-normal tracking-[-0.02em] text-[oklch(97.015%_0.00011_271.152)]">NOT ALL WHO WANDER ARE LOST</div>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="absolute top-[max(90px,3vw)] left-[3vw] z-50 font-semibold tracking-[-0.02em]">{t4}<div className="ml-0 text-[clamp(32px,5vw,64px)] leading-[0.9] font-normal tracking-[-0.02em] text-[oklch(97.015%_0.00011_271.152)]">BUT I SURE AM<sup className="relative top-[0.65em] ml-1 align-top text-[clamp(10px,0.4em,0.4em)] leading-0 font-semibold tracking-[normal] [font-variant-numeric:tabular-nums]">({images.length})</sup></div></div>;
    t6 = <div className="font-geist-mono absolute right-[3vw] bottom-[3vw] z-50 flex items-center text-[0.625rem] tracking-wider text-[oklch(97.015%_0.00011_271.152)] uppercase">scroll to surf</div>;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  let t7;
  if ($[9] !== hoveredIndex || $[10] !== scrollVelocity || $[11] !== scrollX) {
    t7 = <motion.div className="relative flex size-full items-center justify-center perspective-[2000px] perspective-origin-[10%_10%]"><div className="relative flex transform-[translateY(100px)] items-center justify-center transform-3d">{Array.from({
          length: TOTAL_PLANES
        }, (__0, i) => <Plane key={i} index={i} scrollX={scrollX} scrollVelocity={scrollVelocity} isHovered={hoveredIndex === i} onHoverStart={() => setHoveredIndex(i)} onHoverEnd={() => setHoveredIndex(null)} />)}</div></motion.div>;
    $[9] = hoveredIndex;
    $[10] = scrollVelocity;
    $[11] = scrollX;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t3 || $[14] !== t7) {
    t8 = <motion.div ref={containerRef} className="font-geist-sans h-screen w-screen touch-none overflow-hidden bg-black" onPan={t3}>{t5}{t6}{t7}</motion.div>;
    $[13] = t3;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}